'use client';

import { createContext, useState, useContext, useEffect, useMemo, useCallback, ReactNode } from 'react';
import keyBy from 'lodash/keyBy';
import { getContainer } from '@framework/di/getContainer';
import CreditFlowResearchRepository
  from '@/creditflowresearch/infrastructure/repository/CreditFlowResearchRepository';
import useFetchCollection from '@/creditflowresearch/infrastructure/state/useFetchCollection';
import useAutoFetch from '@/creditflowresearch/application/hooks/useAutoFetch';
import useDeepCompareMemo from '@/creditflowresearch/application/hooks/useDeepCompareMemo';
import {
  IMarketStat,
  IMarketStatPayload,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketStat';
import {
  initialStatsTileCollectionFiltersState,
} from '@/creditflowresearch/application/ArticlesAndStats/Stats/StatsTile/constants';
import { mapCategoriesNames } from '@/creditflowresearch/application/ArticlesAndStats/Stats/StatsTile/helpers';
import { IStatsTileServerData } from '@/creditflowresearch/application/ArticlesAndStats/Stats/StatsTile/serverSide/getServerSideStatsTile';
import useStatsTileFilter from '@/creditflowresearch/application/ArticlesAndStats/Stats/StatsTile/useStatsTileFilter';

interface IUseStatsTileProps {
  initialData: IStatsTileServerData,
}

const useStatsTile = ({ initialData: { payload, hasServerError, categories: categoriesList, ...data } }: IUseStatsTileProps) => {
  const container = getContainer();
  const requestService = container.get<CreditFlowResearchRepository>(CreditFlowResearchRepository);
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(!hasServerError);
  const [search, setSearch] = useState('');

  const statsPayload = useStatsTileFilter(payload);

  const mapCategories = useMemo(() => keyBy(categoriesList, 'id'), [categoriesList]);

  const fetchStats = useCallback(
    (payload: IMarketStatPayload) => requestService.getStatsList(payload),
    [requestService],
  );

  const statsData = useFetchCollection<IMarketStat, IMarketStatPayload>(
    fetchStats,
    {
      enabled: !isFirstFetching,
      payload: statsPayload as IMarketStatPayload,
      initialValues: {
        collection: data.collection,
        totalCount: data.totalCount,
        ...initialStatsTileCollectionFiltersState,
      },
    },
  );

  const collectionWithNames = useMemo(
    () => mapCategoriesNames(statsData.collection, mapCategories),
    [statsData.collection, mapCategories],
  );

  const stablePayload = useDeepCompareMemo(statsPayload);

  useAutoFetch(
    () => setIsFirstFetching(false),
    [stablePayload, statsData.page, statsData.debouncedSearchString],
  );

  useEffect(() => {
    statsData.reset();
  }, [data.collection]);

  useEffect(() => statsData.reset, []);

  return {
    statsData: {
      ...statsData,
      collection: collectionWithNames,
    },
    search,
    setSearch,
  };
};

type StatsTileContextType = ReturnType<typeof useStatsTile>;

const StatsTileContext = createContext<StatsTileContextType | null>(null);

export const useStatsTileContext = (): StatsTileContextType => {
  const context = useContext(StatsTileContext);

  if (!context) {
    throw new Error('useStatsTileContext must be used within StatsTileContextProvider');
  }

  return context;
};

interface IStatsTileContextProviderProps {
  children: ReactNode,
  initialData: IStatsTileServerData,
}

const StatsTileContextProvider = ({ children, initialData }: IStatsTileContextProviderProps) => (
  <StatsTileContext.Provider value={ useStatsTile({ initialData }) }>
    { children }
  </StatsTileContext.Provider>
);

export default StatsTileContextProvider;
